import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  Coins,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  Trash2,
  Loader2,
  Receipt,
  ShoppingCart,
  Wallet,
  Truck,
  HandCoins,
  Info,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; }
};

// ──────────── Voucher Modal ────────────
const CapitalVoucherModal = ({ kind, currentCapital, onClose, onSaved }) => {
  const isDeposit = kind === "deposit";
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { requestClose } = useDrafts();

  const hasContent = !!(amount.trim() || note.trim());
  const guardedClose = () => requestClose(hasContent, isDeposit ? "سند قبض لرأس المال" : "سند دفع من رأس المال", onClose);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/capital/vouchers`, { kind, amount: amt, note: note.trim() });
      toast.success(isDeposit ? "تم تسجيل سند القبض لرأس المال" : "تم تسجيل سند الدفع من رأس المال");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="capital-voucher-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`text-white p-4 flex justify-between items-center ${isDeposit ? "bg-gradient-to-l from-[#15803d] to-[#22c55e]" : "bg-gradient-to-l from-[#E85D45] to-[#F97316]"}`}>
          <h3 className="font-bold text-lg flex items-center gap-2">
            {isDeposit ? <ArrowDownToLine className="w-5 h-5" /> : <ArrowUpFromLine className="w-5 h-5" />}
            {isDeposit ? "سند قبض لرأس المال" : "سند دفع من رأس المال"}
          </h3>
          <button onClick={guardedClose} data-testid="cap-voucher-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-[#F8FAFC] rounded-xl p-3 border-2 border-[#E4EBE6]">
            <p className="text-xs text-[#5C6E64]">الرصيد الحالي لرأس المال</p>
            <p className={`font-bold text-lg ${currentCapital < 0 ? "text-[#E85D45]" : "text-[#15803d]"}`} dir="ltr">{fmt(currentCapital)} د.ع</p>
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">المبلغ (د.ع) *</label>
            <input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="cap-voucher-amount" dir="ltr"
              className="w-full px-3 py-3 rounded-xl border-2 border-[#3B82F6] text-lg font-bold focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#5C6E64] block mb-1">ملاحظة</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="cap-voucher-note" rows={2}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]"
              placeholder={isDeposit ? "مثلاً: إيداع رأس مال ابتدائي" : "مثلاً: سحب نقدي للنفقات الشخصية"} />
          </div>
          <p className="text-[11px] text-[#5C6E64] bg-[#FFF7ED] border border-[#FDBA74] rounded-xl p-2 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {isDeposit
              ? "سند القبض يضيف المبلغ مباشرة إلى رصيد رأس المال."
              : "سند الدفع يخصم المبلغ مباشرة من رصيد رأس المال (سحب نقدي خارج المنظومة التلقائية)."}
          </p>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={saving} data-testid="cap-voucher-submit"
            className={`text-white font-bold py-2 px-5 rounded-full flex items-center gap-1 disabled:bg-[#BAE6FD] ${isDeposit ? "bg-[#15803d] hover:bg-[#126b32]" : "bg-[#E85D45] hover:bg-[#d54d35]"}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            تسجيل السند
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────── Main Tab ────────────
const CapitalTab = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState(null); // "deposit"|"withdraw"|null

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/capital`),
        axios.get(`${API}/admin/capital/vouchers`),
      ]);
      setData(r1.data);
      setVouchers(r2.data || []);
    } catch (e) {
      toast.error("فشل تحميل بيانات رأس المال");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteVoucher = async (vid) => {
    if (!(await confirmDialog("حذف هذا السند؟ سيتم إعادة حساب رأس المال."))) return;
    try {
      await axios.delete(`${API}/admin/capital/vouchers/${vid}`);
      toast.success("تم حذف السند");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    }
  };

  const refreshAll = () => { load(); onChanged && onChanged(); };

  const breakdown = data?.breakdown || {};
  const capital = data?.capital || 0;

  return (
    <div className="space-y-3" data-testid="capital-tab" dir="rtl">
      {/* Header — Green */}
      <div className="bg-gradient-to-l from-[#15803d] to-[#22c55e] text-white rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <Coins className="w-5 h-5" />
          <h3 className="font-bold text-lg">رأس المال المتحرّك</h3>
        </div>
        <p className="text-xs opacity-90 mb-3">
          يتحرّك تلقائيًا: ينقص عند الشراء من المُجهّزين، ويزيد عند البيع وتسديد ديون الزبائن. أضف رأس المال الابتدائي أو اسحب نقدًا عبر السندات اليدوية.
        </p>
        <div className="bg-white/15 rounded-xl p-3">
          <p className="text-[11px] opacity-90">الرصيد الحالي لرأس المال</p>
          <p className="font-black text-3xl mt-1" dir="ltr" data-testid="capital-current">{fmt(capital)} د.ع</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setKind("deposit")} data-testid="open-cap-deposit"
          className="bg-[#15803d] hover:bg-[#126b32] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <ArrowDownToLine className="w-4 h-4" /> سند قبض لرأس المال
        </button>
        <button onClick={() => setKind("withdraw")} data-testid="open-cap-withdraw"
          className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <ArrowUpFromLine className="w-4 h-4" /> سند دفع من رأس المال
        </button>
      </div>

      {/* Breakdown */}
      {loading || !data ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#15803d]" /></div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]">
            <h4 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-1">
              <Info className="w-4 h-4" /> تفصيل الحركة (شفافية كاملة)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Row icon={ArrowDownToLine} label="سندات قبض يدوية" value={breakdown.manual_deposits} sign="+" color="#15803d" />
              <Row icon={ArrowUpFromLine} label="سندات دفع يدوية" value={breakdown.manual_withdraws} sign="−" color="#E85D45" />
              <Row icon={ShoppingCart} label="مدفوع لفواتير الشراء" value={breakdown.purchases_paid} sign="−" color="#E85D45" />
              <Row icon={Truck} label="سندات دفع للمُجهّزين" value={breakdown.supplier_payment_out} sign="−" color="#E85D45" />
              <Row icon={Truck} label="سندات استلام من المُجهّزين" value={breakdown.supplier_receipt_in} sign="+" color="#15803d" />
              <Row icon={Wallet} label="مبيعات نقدية مستلَمة" value={breakdown.sales_paid} sign="+" color="#15803d" />
              <Row icon={HandCoins} label="تسديدات ديون الزبائن" value={breakdown.debt_collected} sign="+" color="#15803d" />
            </div>
            <div className="mt-3 pt-3 border-t-2 border-[#E4EBE6] flex items-center justify-between">
              <p className="text-sm font-bold text-[#1E3A5F]">صافي رأس المال الحالي</p>
              <p className={`text-2xl font-black ${capital < 0 ? "text-[#E85D45]" : "text-[#15803d]"}`} dir="ltr">{fmt(capital)} د.ع</p>
            </div>
          </div>

          {/* Manual Vouchers history */}
          <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]">
            <h4 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-1">
              <Receipt className="w-4 h-4" /> سجل السندات اليدوية ({vouchers.length})
            </h4>
            {vouchers.length === 0 ? (
              <p className="text-center text-[#5C6E64] text-sm py-6">لا توجد سندات يدوية بعد.</p>
            ) : (
              <div className="space-y-2">
                {vouchers.map((v) => {
                  const isDep = v.kind === "deposit";
                  return (
                    <div key={v.id} data-testid={`cap-voucher-${v.id}`}
                      className={`rounded-xl p-3 border-2 flex items-center justify-between gap-2 ${isDep ? "bg-[#ECFDF5] border-[#86efac]" : "bg-[#FEF2F2] border-[#FCA5A5]"}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isDep ? <ArrowDownToLine className="w-5 h-5 text-[#15803d] shrink-0" /> : <ArrowUpFromLine className="w-5 h-5 text-[#E85D45] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm">{isDep ? "سند قبض لرأس المال" : "سند دفع من رأس المال"}</p>
                          <p className="text-[11px] text-[#5C6E64] truncate">
                            {fmtDate(v.created_at)}
                            {v.note && <> · {v.note}</>}
                          </p>
                        </div>
                      </div>
                      <p className={`font-black text-lg shrink-0 ${isDep ? "text-[#15803d]" : "text-[#E85D45]"}`} dir="ltr">
                        {isDep ? "+" : "−"}{fmt(v.amount)}
                      </p>
                      <button onClick={() => deleteVoucher(v.id)} data-testid={`del-cap-voucher-${v.id}`}
                        className="text-[#E85D45] hover:bg-white rounded p-1 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {kind && (
        <CapitalVoucherModal
          kind={kind}
          currentCapital={capital}
          onClose={() => setKind(null)}
          onSaved={() => { setKind(null); refreshAll(); }}
        />
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, value, sign, color }) => (
  <div className="flex items-center justify-between bg-[#F8FAFC] rounded-xl p-2.5 border border-[#E4EBE6]">
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <p className="text-xs text-[#1E3A5F] truncate">{label}</p>
    </div>
    <p className="font-bold text-sm shrink-0" style={{ color }} dir="ltr">
      {sign}{fmt(value)}
    </p>
  </div>
);

export default CapitalTab;
